import React, { ReactElement, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearSession } from '@az/auth-ping';
import { useTranslation } from 'react-i18next';
import { deleteSSOSessions } from '../../../userSSOSessions';

import NavMenu from '../../layout/NavMenu';
import StoreLocation from '../../pages/StoreLocation';
import ConfirmationScreen from '../../layout/ConfirmationScreen';

import { getDriverLogoffStatus, selectCurrentLanguage, selectPickerStore } from '../../../redux/selectors';
import {
  getCompletedRecalls,
  getCompletedEPOrders,
  getPickToursInProgress,
} from '../../../redux/actions/orders.actions';
import { setDriverLogoffId } from '../../../redux/actions/driverLogoff.actions';

import { determineDeviceType, getUID } from '../../../utils';

import {
  DESKTOP,
  CT40,
  TC75,
  USER_DATA,
  STORE_RECALL_DATA,
  LAST_LOGIN,
  RECALL_HISTORY_DAYS,
  MOBILE_PRINTER_NUMBER,
  USER_LOGOUT_MSG,
} from '../../../constants';

import { AppLogType, NavMenuTab, UserData } from '../../../types';

import styles from './Waffle.css';
import { sendLog } from '../../../redux/actions/log.actions';

const square: ReactElement = <span className={styles.square} />;

const row: ReactElement = (
  <span className={styles.row}>
    {square}
    {square}
    {square}
  </span>
);

type Props = {
  activeNavTab: NavMenuTab;
  storeInputValue: string;
  firstStoreID: string;
  hasOverstockOrder: boolean;
  isOverstockEnabled: boolean;
  setActiveNavTab: (value: SetStateAction<NavMenuTab>) => void;
  setStoreInputValue: (value: SetStateAction<string>) => void;
  setFirstStoreID: (value: SetStateAction<string>) => void;
  createOverstockPlaceholder: () => void;
  clearSelected: () => void;
  clearAndFetchOrders: () => void;
  languageOnOpenRef: React.MutableRefObject<string>;
};

const Waffle = ({
  activeNavTab,
  storeInputValue,
  firstStoreID,
  hasOverstockOrder,
  isOverstockEnabled,
  setActiveNavTab,
  setStoreInputValue,
  setFirstStoreID,
  createOverstockPlaceholder,
  clearSelected,
  clearAndFetchOrders,
  languageOnOpenRef,
}: Props): ReactElement => {
  const [isOpened, setIsOpened] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const dispatch = useDispatch();

  const pickerStore = useSelector(selectPickerStore);
  const driverLogoffStatus = useSelector(getDriverLogoffStatus);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const { t } = useTranslation();
  const translationRoot = 'WAFFLE_MENU';

  const isNavTabDisabled =
    !pickerStore.storeID || activeNavTab === 'generateOverstock' || activeNavTab === 'storeLocation';

  useEffect(() => {
    if (driverLogoffStatus !== null) {
      localStorage.removeItem(`${LAST_LOGIN}_${getUID() || ''}`);
      localStorage.removeItem('updateSSOCalled');
      clearSession();
    }
  }, [driverLogoffStatus]);

  const handleOnClick = (shouldCheckLanguage: boolean = true) => {
    if (!isOpened) {
      // Waffle is opening - store current language for comparison later
      // eslint-disable-next-line no-param-reassign
      languageOnOpenRef.current = currentLanguage;
    } else if (shouldCheckLanguage && languageOnOpenRef.current !== currentLanguage) {
      // Waffle is closing AND language changed - refresh orders
      clearAndFetchOrders();
    }
    setIsOpened(prevIsOpened => !prevIsOpened);
    setIsAnimating(prevIsAnimating => !prevIsAnimating);
  };

  // Helper function to handle nav tab toggle
  const handleNavTabToggle = (tabName: NavMenuTab, onOpen?: () => void) => {
    // Don't check language when opening a nav tab (waffle stays open conceptually)
    handleOnClick(false);
    if (activeNavTab === tabName) {
      clearAndFetchOrders();
      setActiveNavTab(null);
    } else {
      if (onOpen) {
        onOpen();
      }
      setActiveNavTab(tabName);
    }
  };

  const driverLogoff = () => {
    localStorage.removeItem(STORE_RECALL_DATA);
    localStorage.removeItem(MOBILE_PRINTER_NUMBER);
    const deviceType = determineDeviceType();
    if (deviceType === DESKTOP) {
      localStorage.removeItem(`${LAST_LOGIN}_${getUID() || ''}`);
      localStorage.removeItem('updateSSOCalled');
      clearSession();
    }
    if (deviceType === CT40 || deviceType === TC75) {
      const userData = sessionStorage.getItem(USER_DATA);
      if (userData) {
        const parsedUserData: UserData = JSON.parse(userData);
        const { sub } = parsedUserData;
        dispatch(setDriverLogoffId(sub));
      }
    }
  };

  const determineTab = () => {
    if (activeNavTab === 'storeLocation') {
      return (
        <StoreLocation
          storeInputValue={storeInputValue}
          firstStoreID={firstStoreID}
          setStoreInputValue={setStoreInputValue}
          setFirstStoreID={setFirstStoreID}
          setActiveNavTab={setActiveNavTab}
        />
      );
    }
    if (activeNavTab === 'generateOverstock') {
      return (
        <ConfirmationScreen
          confirmationText={t(`${translationRoot}.GENERATE_ORDER_CONFIRMATION`)}
          confirmationButtonText={t(`${translationRoot}.GENERATE_ORDER_BUTTON`)}
          handleConfirm={createOverstockPlaceholder}
        />
      );
    }
    return null;
  };

  return (
    <>
      <button
        type="button"
        onClick={() => handleOnClick(isOpened)} // Check language only when closing
        className={styles.button}
        data-cy="waffle-button"
      >
        <span className={styles.container}>
          {row}
          {row}
          {row}
        </span>
      </button>
      {isOpened || isAnimating ? (
        <NavMenu
          isOpened={isOpened}
          navTabs={[
            {
              id: 'storeLocation',
              text: t(`${translationRoot}.STORE_LOCATION`),
              disabled: activeNavTab === 'storeLocation' || pickerStore.shouldPromptStoreSelection,
              hasArrow: true,
              onClick: () => {
                handleOnClick(false); // Don't check - transitioning to store Location
                setActiveNavTab('storeLocation');
              },
            },
            {
              id: 'generateOverstock',
              text: t(`${translationRoot}.GENERATE_OVERSTOCK`),
              disabled:
                !isOverstockEnabled ||
                !pickerStore.storeID ||
                activeNavTab === 'generateOverstock' ||
                activeNavTab === 'storeLocation' ||
                hasOverstockOrder,
              hasArrow: true,
              onClick: () => {
                handleOnClick(false); // Don't check - transitioning to generateOverstock tab
                setActiveNavTab('generateOverstock');
              },
            },
            {
              id: 'recallsHistory',
              text: t(
                `${translationRoot}.${
                  activeNavTab === 'recallsHistory' ? 'RETURN_TO_PENDING_ORDERS' : 'RECALLS_HISTORY'
                }`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                clearSelected();
                handleNavTabToggle('recallsHistory', () => {
                  dispatch(
                    getCompletedRecalls({
                      fulfillmentStoreID: pickerStore.storeID || '',
                      daysAgo: RECALL_HISTORY_DAYS,
                      pageNumber: 0,
                    })
                  );
                });
              },
            },
            {
              id: 'reprintEPLabel',
              text: t(
                `${translationRoot}.${
                  activeNavTab === 'reprintEPLabel' ? 'RETURN_TO_PENDING_ORDERS' : 'REPRINT_EP_LABELS'
                }`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                handleNavTabToggle('reprintEPLabel', () => {
                  dispatch(getCompletedEPOrders({ fulfillmentStoreID: pickerStore.storeID || '', pageNumber: 1 }));
                });
              },
            },
            {
              id: 'mobilePrinter',
              text: t(
                `${translationRoot}.${activeNavTab === 'mobilePrinter' ? 'RETURN_TO_PENDING_ORDERS' : 'MOBILE_PRINTER'}`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                handleNavTabToggle('mobilePrinter');
              },
            },
            {
              id: 'resetPickTour',
              text: t(
                `${translationRoot}.${
                  activeNavTab === 'resetPickTour' ? 'RETURN_TO_PENDING_ORDERS' : 'RESET_PICK_TOUR'
                }`
              ),
              disabled: isNavTabDisabled,
              onClick: () => {
                handleNavTabToggle('resetPickTour', () => {
                  dispatch(getPickToursInProgress({ fulfillmentStoreID: pickerStore.storeID || '' }));
                });
              },
            },
            {
              id: 'logOut',
              text: t(`${translationRoot}.LOGOUT`),
              onClick: async () => {
                const deviceType = determineDeviceType();
                if (deviceType === CT40 || deviceType === TC75) {
                  try {
                    await deleteSSOSessions();
                  } catch (error) {
                    console.log('Error delete SSO sessions', error);
                  }
                }
                handleOnClick();
                driverLogoff();
                dispatch(sendLog({ logEvent: AppLogType.LOG_OUT, logMessage: USER_LOGOUT_MSG }));
              },
            },
          ]}
          onCloseMenu={handleOnClick} // Default true - will check language
          setIsAnimating={setIsAnimating}
        />
      ) : null}
      {determineTab()}
    </>
  );
};

export default Waffle;
